// Tasks for a venue: lists, several people per task (shared or each person), comments.
// Works before the task-rework database update too (falls back to one person per task).
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/src/lib/supabase';
import { log } from '@/src/lib/logger';
import type { TaskLink } from '@/src/lib/taskLinks';

export interface TaskAssignee { user_id: string; completed_at: string | null }
export interface TaskList { id: string; name: string; position: number }
export type TaskMode = 'shared' | 'individual';

export interface TaskRow {
  id: string;
  title: string;
  status: string;
  priority: string | null;
  due_at: string | null;
  assigned_to: string | null;
  acceptance_status?: string | null;
  description?: string | null;
  created_at?: string;
  completed_at?: string | null;
  link?: TaskLink | null;
  list_id?: string | null;
  mode?: TaskMode;
  /** Everyone on the task (falls back to assigned_to before the database update). */
  assignees: TaskAssignee[];
  comment_count: number;
}

export interface TaskComment { id: string; task_id: string; user_id: string | null; body: string; created_at: string }

export interface CreateTaskInput {
  title: string;
  description?: string | null;
  priority?: string;
  due_at?: string | null;
  /** Legacy single person; prefer assigneeIds. */
  assigned_to?: string | null;
  assigneeIds?: string[];
  mode?: TaskMode;
  list_id?: string | null;
  venue_id: string;
  link?: TaskLink | null;
}

const BASE = 'id, title, description, status, priority, due_at, assigned_to, acceptance_status, created_at, completed_at';
const FULL = `${BASE}, link, list_id, mode, task_assignees(user_id, completed_at), task_comments(count)`;

type Raw = Omit<TaskRow, 'assignees' | 'comment_count'> & { task_assignees?: TaskAssignee[]; task_comments?: Array<{ count: number }> };

function shape(r: Raw, rework: boolean): TaskRow {
  const assignees = rework && r.task_assignees ? r.task_assignees : r.assigned_to ? [{ user_id: r.assigned_to, completed_at: r.status === 'done' ? (r.completed_at ?? null) : null }] : [];
  const { task_assignees: _a, task_comments: tc, ...rest } = r;
  return { ...rest, mode: r.mode ?? 'shared', assignees, comment_count: tc?.[0]?.count ?? 0 };
}

async function fetchAll(venueId: string): Promise<{ tasks: TaskRow[]; rework: boolean }> {
  const since = new Date(Date.now() - 14 * 86_400_000).toISOString();
  const run = (cols: string) => Promise.all([
    supabase.from('tasks').select(cols).eq('venue_id', venueId).neq('status', 'done').order('due_at', { ascending: true, nullsFirst: false }).limit(400),
    supabase.from('tasks').select(cols).eq('venue_id', venueId).eq('status', 'done').gte('completed_at', since).order('completed_at', { ascending: false }).limit(200),
  ]);
  let [open, done] = await run(FULL);
  let rework = true;
  if (open.error) {
    rework = false;
    [open, done] = await run(`${BASE}, link`);
    if (open.error) [open, done] = await run(BASE);
  }
  if (open.error) log.warn('tasks load failed', { code: open.error.code });
  const rows = [...((open.data ?? []) as unknown as Raw[]), ...((done.data ?? []) as unknown as Raw[])];
  return { tasks: rows.map((r) => shape(r, rework)), rework };
}

export function useTasks(venueId: string | null, opts: { mineOnly?: boolean; userId?: string } = {}) {
  const [all, setAll] = useState<TaskRow[]>([]);
  const [lists, setLists] = useState<TaskList[]>([]);
  const [rework, setRework] = useState(true);
  const [loading, setLoading] = useState(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reload = useCallback(async () => {
    if (!venueId) return;
    const [{ tasks, rework: rw }, l] = await Promise.all([
      fetchAll(venueId),
      supabase.from('task_lists').select('id, name, position').eq('venue_id', venueId).order('position').order('created_at'),
    ]);
    setAll(tasks); setRework(rw); setLists(l.error ? [] : ((l.data ?? []) as TaskList[]));
    setLoading(false);
  }, [venueId]);

  useEffect(() => {
    if (!venueId) { setAll([]); setLoading(false); return; }
    setLoading(true);
    void reload();
    const soon = () => { if (timer.current) clearTimeout(timer.current); timer.current = setTimeout(() => void reload(), 300); };
    const ch = supabase.channel(`tasks:${venueId}:${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks', filter: `venue_id=eq.${venueId}` }, soon)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_assignees', filter: `venue_id=eq.${venueId}` }, soon)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_comments', filter: `venue_id=eq.${venueId}` }, soon)
      .subscribe();
    return () => { if (timer.current) clearTimeout(timer.current); supabase.removeChannel(ch); };
  }, [venueId, reload]);

  const isMine = (t: TaskRow, uid: string) => t.assignees.some((a) => a.user_id === uid) || t.assigned_to === uid;
  const tasks = opts.mineOnly && opts.userId ? all.filter((t) => isMine(t, opts.userId!)) : all;

  async function createTask(input: CreateTaskInput): Promise<{ error: string | null }> {
    const ids = [...new Set([...(input.assigneeIds ?? []), ...(input.assigned_to ? [input.assigned_to] : [])])];
    const base: Record<string, unknown> = {
      title: input.title, description: input.description ?? null, priority: input.priority ?? 'normal', due_at: input.due_at ?? null,
      assigned_to: ids[0] ?? null, venue_id: input.venue_id, status: 'open', ...(input.link ? { link: input.link } : {}),
    };
    if (rework) {
      const { data, error } = await supabase.from('tasks').insert({ ...base, mode: ids.length > 1 ? (input.mode ?? 'shared') : 'shared', list_id: input.list_id ?? null }).select('id').single();
      if (error || !data) return { error: error?.message ?? 'Could not create the task' };
      if (ids.length) {
        const { error: aErr } = await supabase.from('task_assignees').insert(ids.map((uid) => ({ task_id: data.id, user_id: uid, venue_id: input.venue_id })));
        if (aErr) return { error: `Task created, but adding people failed: ${aErr.message}` };
      }
      await reload();
      return { error: null };
    }
    // Before the database update: one task per person.
    for (const uid of ids.length ? ids : [null]) {
      const { error } = await supabase.from('tasks').insert({ ...base, assigned_to: uid });
      if (error) return { error: error.message };
    }
    await reload();
    return { error: null };
  }

  async function updateTask(id: string, patch: Partial<Pick<TaskRow, 'title' | 'description' | 'priority' | 'due_at' | 'list_id' | 'mode' | 'link'>>, assigneeIds?: string[]): Promise<{ error: string | null }> {
    const task = all.find((t) => t.id === id);
    const upd: Record<string, unknown> = { ...patch };
    if (assigneeIds) upd.assigned_to = assigneeIds[0] ?? null;
    if (!rework) { delete upd.list_id; delete upd.mode; }
    const { error } = await supabase.from('tasks').update(upd).eq('id', id);
    if (error) return { error: error.message };
    if (assigneeIds && rework && task) {
      const before = task.assignees.map((a) => a.user_id);
      const remove = before.filter((u) => !assigneeIds.includes(u));
      const add = assigneeIds.filter((u) => !before.includes(u));
      if (remove.length) await supabase.from('task_assignees').delete().eq('task_id', id).in('user_id', remove);
      if (add.length && venueId) await supabase.from('task_assignees').insert(add.map((u) => ({ task_id: id, user_id: u, venue_id: venueId })));
    }
    await reload();
    return { error: null };
  }

  /** Tick or untick. "Each person" tasks: ticks your own part; managers not on it tick the whole task. */
  async function toggleDone(task: TaskRow, userId: string): Promise<{ error: string | null }> {
    const now = new Date().toISOString();
    const mine = task.assignees.find((a) => a.user_id === userId);
    if (rework && task.mode === 'individual' && mine) {
      const next = mine.completed_at ? null : now;
      setAll((p) => p.map((t) => (t.id === task.id ? { ...t, assignees: t.assignees.map((a) => (a.user_id === userId ? { ...a, completed_at: next } : a)) } : t)));
      const { error } = await supabase.from('task_assignees').update({ completed_at: next }).eq('task_id', task.id).eq('user_id', userId);
      if (error) void reload();
      return { error: error?.message ?? null };
    }
    const done = task.status === 'done';
    setAll((p) => p.map((t) => (t.id === task.id ? { ...t, status: done ? 'open' : 'done', completed_at: done ? null : now } : t)));
    const { error } = await supabase.from('tasks').update({ status: done ? 'open' : 'done', completed_at: done ? null : now }).eq('id', task.id);
    if (error) void reload();
    else if (rework && task.mode === 'individual' && done) await supabase.from('task_assignees').update({ completed_at: null }).eq('task_id', task.id);
    return { error: error?.message ?? null };
  }

  async function completeTask(id: string): Promise<{ error: string | null }> {
    const t = all.find((x) => x.id === id);
    if (!t || t.status === 'done') return { error: null };
    return toggleDone(t, opts.userId ?? '');
  }

  async function deleteTask(id: string): Promise<{ error: string | null }> {
    setAll((p) => p.filter((t) => t.id !== id));
    const { error } = await supabase.from('tasks').delete().eq('id', id);
    if (error) void reload();
    return { error: error?.message ?? null };
  }

  // Lists
  async function createList(name: string): Promise<{ id?: string; error: string | null }> {
    if (!venueId) return { error: 'no venue' };
    const { data, error } = await supabase.from('task_lists').insert({ venue_id: venueId, name: name.trim().slice(0, 60), position: lists.length }).select('id').single();
    if (error) return { error: /task_lists/.test(error.message) ? 'Task lists need the database update first.' : error.message };
    await reload();
    return { id: (data as { id: string }).id, error: null };
  }
  async function renameList(id: string, name: string) {
    setLists((p) => p.map((l) => (l.id === id ? { ...l, name } : l)));
    await supabase.from('task_lists').update({ name: name.trim().slice(0, 60) }).eq('id', id);
  }
  async function deleteList(id: string) {
    setLists((p) => p.filter((l) => l.id !== id));
    await supabase.from('task_lists').delete().eq('id', id);
    await reload();
  }

  // Comments
  async function loadComments(taskId: string): Promise<TaskComment[]> {
    const { data } = await supabase.from('task_comments').select('id, task_id, user_id, body, created_at').eq('task_id', taskId).order('created_at');
    return (data ?? []) as TaskComment[];
  }
  async function addComment(taskId: string, userId: string, body: string): Promise<{ error: string | null }> {
    if (!venueId) return { error: 'no venue' };
    const { error } = await supabase.from('task_comments').insert({ task_id: taskId, venue_id: venueId, user_id: userId, body: body.trim().slice(0, 2000) });
    if (!error) setAll((p) => p.map((t) => (t.id === taskId ? { ...t, comment_count: t.comment_count + 1 } : t)));
    return { error: error ? (/task_comments/.test(error.message) ? 'Comments need the database update first.' : error.message) : null };
  }
  async function deleteComment(id: string, taskId: string) {
    await supabase.from('task_comments').delete().eq('id', id);
    setAll((p) => p.map((t) => (t.id === taskId ? { ...t, comment_count: Math.max(0, t.comment_count - 1) } : t)));
  }

  return {
    tasks, lists, loading, rework,
    createTask, updateTask, toggleDone, completeTask, deleteTask,
    createList, renameList, deleteList,
    loadComments, addComment, deleteComment,
    reload,
  };
}

/** A new task already filled in (e.g. from a Team Chats message). */
export type TaskPreset = { title: string; description: string; assigneeIds?: string[]; dueDate?: string | null; dueTime?: string | null; priority?: string };
